"use client";

import { useReducedMotion } from "framer-motion";
import Image from "next/image";
import { Fragment, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

/**
 * The /academic-research hero centerpiece: a classical institution facade drawn
 * the same way as the /desktop CRT (cream body, tan borders, navy recesses, soft
 * blue shadow), crossed with the /experts lanyard badge (cycling expert face
 * photos with a glaze-over swap, a verified tick, and ID/LI/EM/IP attestations).
 * A pediment sits over a colonnade whose arched niches each rotate through real
 * expert headshots. Purely decorative; aria-hidden on the wrapper.
 */

const FACES = [
  "/marketing/experts/ai-training-headshot.jpg",
  "/marketing/experts/expert-9.png",
  "/marketing/experts/market-research-headshot.jpg",
  "/marketing/experts/expert-3.png",
  "/marketing/experts/expert-7.png",
  "/marketing/experts/expert-5.png",
  "/marketing/experts/expert-2.png",
  "/marketing/experts/expert-4.png",
];

const DISCIPLINES = [
  "PSYCHOLOGY",
  "ECONOMICS",
  "LINGUISTICS",
  "PUBLIC HEALTH",
  "NEUROSCIENCE",
  "POLITICAL SCI",
];

/** Each niche reads from a different offset so three distinct faces show. */
const NICHE_OFFSETS = [0, 3, 5];

const CYCLE_MS = 2800;
const GLAZE_MS = 620;
const SOFT_SHADOW =
  "shadow-[0_14px_40px_rgba(0,6,183,0.16),_0_4px_10px_rgba(0,6,183,0.08)]";

/**
 * Advances a shared gallery step forever, raising `glazing` for the swap window
 * so the faces blur out and back in together. Freezes on a still for reduced
 * motion so the facade reads as a finished illustration.
 */
function useGallery() {
  const reduced = Boolean(useReducedMotion());
  const [step, setStep] = useState(0);
  const [glazing, setGlazing] = useState(false);
  const timeouts = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (reduced) {
      return;
    }
    const interval = setInterval(() => {
      setGlazing(true);
      const swap = setTimeout(() => {
        setStep((s) => s + 1);
        const clear = setTimeout(() => {
          setGlazing(false);
        }, 140);
        timeouts.current.push(clear);
      }, GLAZE_MS - 140);
      timeouts.current.push(swap);
    }, CYCLE_MS);

    return () => {
      clearInterval(interval);
      timeouts.current.forEach(clearTimeout);
      timeouts.current = [];
    };
  }, [reduced]);

  return { step, glazing, reduced };
}

const SCRAMBLE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ#$@*&%";
const SCRAMBLE_MS = 700;

/** Settles `target` from noise to clear text whenever `trigger` changes. */
function useScrambled(target: string, trigger: number, reduced: boolean) {
  const [display, setDisplay] = useState(target);

  useEffect(() => {
    if (reduced) {
      setDisplay(target);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / SCRAMBLE_MS);
      const out = target
        .split("")
        .map((c, i) => {
          if (c === " ") {
            return c;
          }
          const lockAt = (i / Math.max(target.length, 1)) * 0.6 + 0.3;
          if (t >= lockAt) {
            return c;
          }
          return SCRAMBLE_CHARS[
            Math.floor(Math.random() * SCRAMBLE_CHARS.length)
          ]!;
        })
        .join("");
      setDisplay(out);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setDisplay(target);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trigger is the intentional re-run signal
  }, [trigger, reduced]);

  return display;
}

function VerifiedTick() {
  return (
    <div className="bg-marketing-blue border-marketing-cream absolute right-1 bottom-1 z-20 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2">
      <svg
        width="9"
        height="9"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M2 6.2 L4.6 8.8 L10 2.8"
          stroke="var(--color-marketing-cream)"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function FaceNiche({
  src,
  glazing,
  reduced,
}: {
  src: string;
  glazing: boolean;
  reduced: boolean;
}) {
  return (
    <div className="relative flex-1">
      <div className="border-marketing-border bg-marketing-navy relative h-full w-full overflow-hidden rounded-t-[34px] rounded-b-[5px] border">
        <div
          className={cn(
            "absolute inset-0",
            !reduced && (glazing ? "academy-glaze" : "academy-clear"),
          )}
        >
          <Image
            key={src}
            src={src}
            alt=""
            fill
            sizes="110px"
            className={cn("object-cover", !reduced && "academy-photo-in")}
            draggable={false}
          />
        </div>

        {/* Subtle CRT scanlines, echoing the /desktop tube */}
        <div
          className="pointer-events-none absolute inset-0 z-10 opacity-[0.1]"
          style={{
            background:
              "repeating-linear-gradient(0deg, transparent 0 2px, rgba(255,255,240,0.6) 2px 3px)",
          }}
        />

        <VerifiedTick />
      </div>
    </div>
  );
}

function Column() {
  return (
    <div className="flex w-[18px] shrink-0 flex-col items-center self-stretch">
      <div className="border-marketing-border bg-marketing-cream h-[7px] w-full rounded-t-[3px] border" />
      <div className="border-marketing-border bg-marketing-cream-light relative w-[13px] flex-1 border-x">
        <span className="bg-marketing-border/50 absolute inset-y-1.5 left-[3px] w-px" />
        <span className="bg-marketing-border/50 absolute inset-y-1.5 right-[3px] w-px" />
      </div>
      <div className="border-marketing-border bg-marketing-cream h-[7px] w-full rounded-b-[2px] border" />
    </div>
  );
}

const ATTESTATIONS = ["ID", "LI", "EM", "IP"];

function AttestationPlaque() {
  return (
    <div className="border-marketing-border bg-marketing-cream mx-auto mt-2 flex w-fit items-center gap-1 rounded-full border px-2 py-1">
      {ATTESTATIONS.map((label) => (
        <div
          key={label}
          className="border-marketing-border bg-marketing-cream-light flex h-[16px] items-center justify-center gap-0.5 rounded-full border px-1.5"
        >
          <svg
            width="6"
            height="6"
            viewBox="0 0 6 6"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M0.8 3 L2.4 4.6 L5.2 1.4"
              stroke="var(--color-marketing-accent-green-dark)"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
          <span className="text-marketing-text-secondary font-mono text-[7px] tracking-wider">
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function AcademicBuilding() {
  const { step, glazing, reduced } = useGallery();
  const label = useScrambled(
    DISCIPLINES[step % DISCIPLINES.length]!,
    step,
    reduced,
  );

  return (
    <div className="relative w-[420px] select-none">
      <style>{`
        @keyframes academy-photo-in {
          0% { opacity: 0; filter: blur(5px) saturate(0.5); transform: scale(1.05); }
          100% { opacity: 1; filter: blur(0) saturate(1); transform: scale(1); }
        }
        .academy-photo-in { animation: academy-photo-in 0.7s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .academy-glaze { filter: blur(2px) saturate(0.7) brightness(1.05); transition: filter 180ms ease-in; }
        .academy-clear { filter: blur(0) saturate(1) brightness(1); transition: filter 320ms ease-out; }
      `}</style>

      {/* Jefferson Memorial: smooth dome on stepped courses over a round,
          colonnaded rotunda. */}
      <svg
        width="360"
        height="198"
        viewBox="0 0 360 198"
        className="mx-auto block"
        aria-hidden="true"
      >
        {/* Rotunda drum the dome sits on */}
        <rect
          x="62"
          y="110"
          width="236"
          height="86"
          rx="6"
          fill="var(--color-marketing-cream)"
          stroke="var(--color-marketing-border)"
          strokeWidth="2"
        />
        {/* Rotunda colonnade hinted with faint verticals */}
        <g
          stroke="var(--color-marketing-border)"
          strokeWidth="1"
          opacity="0.22"
        >
          {[74, 96, 118, 140, 162, 184, 206, 228, 250, 272, 286].map((x) => (
            <line key={x} x1={x} y1="118" x2={x} y2="188" />
          ))}
        </g>
        {/* Base molding ringing the rotunda */}
        <line
          x1="58"
          y1="190"
          x2="302"
          y2="190"
          stroke="var(--color-marketing-border)"
          strokeWidth="2"
          strokeLinecap="round"
        />
        {/* Stepped marble courses at the dome base (widest at the bottom) */}
        <rect
          x="70"
          y="108"
          width="220"
          height="10"
          rx="2"
          fill="var(--color-marketing-cream)"
          stroke="var(--color-marketing-border)"
          strokeWidth="2"
        />
        <rect
          x="78"
          y="98"
          width="204"
          height="11"
          rx="2"
          fill="var(--color-marketing-cream)"
          stroke="var(--color-marketing-border)"
          strokeWidth="2"
        />
        <rect
          x="86"
          y="88"
          width="188"
          height="11"
          rx="2"
          fill="var(--color-marketing-cream)"
          stroke="var(--color-marketing-border)"
          strokeWidth="2"
        />
        {/* Smooth hemispherical dome cap */}
        <path
          d="M84 89 A96 79 0 0 1 276 89 Z"
          fill="var(--color-marketing-cream)"
          stroke="var(--color-marketing-border)"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        {/* Faint courses curving across the dome to show its form */}
        <g
          stroke="var(--color-marketing-border)"
          strokeWidth="1"
          opacity="0.16"
          fill="none"
        >
          <path d="M92 80 A88 70 0 0 1 268 80" />
          <path d="M110 64 A70 56 0 0 1 250 64" />
        </g>
      </svg>

      {/* Entablature with the rotating discipline */}
      <div
        className={cn(
          "border-marketing-border bg-marketing-cream mx-auto -mt-2 w-[372px] rounded-[6px] border px-3 py-2",
          SOFT_SHADOW,
        )}
      >
        <div className="mb-1.5 flex justify-center gap-[6px]">
          {Array.from({ length: 13 }).map((_, k) => (
            <span
              // eslint-disable-next-line react/no-array-index-key -- static decorative triglyphs
              key={k}
              className="bg-marketing-border/60 h-1.5 w-[3px] rounded-full"
            />
          ))}
        </div>
        <p className="text-marketing-text text-center font-mono text-[11px] tracking-[0.3em] uppercase">
          {label}
        </p>
      </div>

      {/* Colonnade: columns interleaved with face niches */}
      <div className="relative">
        <div
          className={cn(
            "border-marketing-border bg-marketing-cream mx-auto mt-1.5 w-[360px] rounded-[10px] border p-3",
            SOFT_SHADOW,
          )}
        >
          <div className="flex h-[130px] items-stretch gap-1.5">
            <Column />
            {NICHE_OFFSETS.map((offset, idx) => (
              // eslint-disable-next-line react/no-array-index-key -- fixed niche positions
              <Fragment key={idx}>
                <FaceNiche
                  src={FACES[(step + offset) % FACES.length]!}
                  glazing={glazing}
                  reduced={reduced}
                />
                <Column />
              </Fragment>
            ))}
          </div>
        </div>

        {/* VERIFIED stamp, echoing the badge's rubber-stamp mark */}
        <div
          className="text-marketing-accent-green-dark border-marketing-accent-green-dark absolute -top-1 -right-1 rotate-[10deg] rounded-[2px] border-2 bg-[rgba(255,255,240,0.85)] px-1.5 py-0.5 font-mono text-[8px] font-bold tracking-[0.2em] uppercase"
          aria-hidden="true"
        >
          Verified
        </div>
      </div>

      <AttestationPlaque />

      {/* Stylobate steps */}
      <div className="mt-2 flex flex-col items-center">
        <div className="border-marketing-border bg-marketing-cream h-2 w-[360px] rounded-[3px] border" />
        <div className="border-marketing-border bg-marketing-cream -mt-px h-2 w-[392px] rounded-[3px] border" />
        <div
          className={cn(
            "border-marketing-border bg-marketing-cream -mt-px h-2.5 w-[420px] rounded-[4px] border",
            SOFT_SHADOW,
          )}
        />
      </div>
    </div>
  );
}
